import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { RefreshCw, Unplug, Plus, CheckCircle2, AlertTriangle, ShieldOff } from 'lucide-react';
import { useTheme } from '../../lib/theme';
import { SettingsCard, primaryBtn, outlineBtn } from '../settings/_shared';
import {
  getQbConnectUrl,
  listQbConnections,
  refreshQbConnection,
  deleteQbConnection,
} from '../../lib/api';
import QuickBooksMappings from './QuickBooksMappings';

const QB_LOGO = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="11" fill="#2CA01C" />
    <text
      x="12" y="16" textAnchor="middle"
      fontFamily="system-ui, -apple-system" fontWeight="900"
      fontSize="12" fill="white"
    >qb</text>
  </svg>
);

function HealthBadge({ health, colors }) {
  const map = {
    ok: { label: 'Healthy', bg: '#E7F7EE', fg: '#117A3C', Icon: CheckCircle2 },
    expiring_soon: { label: 'Expiring soon', bg: '#FFF4D6', fg: '#8A5A00', Icon: AlertTriangle },
    expired: { label: 'Expired', bg: '#FDECEC', fg: '#B42318', Icon: ShieldOff },
  };
  const row = map[health] || map.ok;
  const { Icon } = row;
  return (
    <span
      data-testid={`qb-health-${health}`}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: '3px 10px', borderRadius: 999,
        background: row.bg, color: row.fg,
        fontSize: 11, fontWeight: 700, letterSpacing: '0.01em',
      }}
    >
      <Icon size={12} />{row.label}
    </span>
  );
}

function _fmtTime(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' });
  } catch {
    return '—';
  }
}

export default function QuickBooksCard({ onToast }) {
  const { colors } = useTheme();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [connections, setConnections] = useState([]);
  const [configured, setConfigured] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busyConnect, setBusyConnect] = useState(false);
  const [busyRefresh, setBusyRefresh] = useState(null);
  const [busyDelete, setBusyDelete] = useState(null);
  const [selectedId, setSelectedId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listQbConnections();
      setConnections(data.connections || []);
      setConfigured(Boolean(data.configured));
      if ((data.connections || []).length && !selectedId) {
        setSelectedId(data.connections[0].id);
      }
    } catch (e) {
      onToast?.(`Could not load connections: ${e.detail || 'network error'}`);
    } finally {
      setLoading(false);
    }
  }, [onToast, selectedId]);

  useEffect(() => { load(); }, [load]);

  // One-time success toast on ?qb=connected (clears the param).
  useEffect(() => {
    const qb = params.get('qb');
    const msg = params.get('qb_msg');
    if (!qb) return;
    if (qb === 'connected') {
      onToast?.('Connected to QuickBooks');
    } else if (qb === 'error') {
      onToast?.(msg ? `QuickBooks error: ${msg}` : 'Could not connect to QuickBooks');
    }
    // Clear qb params while preserving the hash.
    const nextSearch = new URLSearchParams(params);
    nextSearch.delete('qb');
    nextSearch.delete('qb_msg');
    const qs = nextSearch.toString();
    navigate(`${location.pathname}${qs ? `?${qs}` : ''}${location.hash || '#integrations'}`, { replace: true });
  }, []);

  const onConnect = async () => {
    setBusyConnect(true);
    try {
      const { url } = await getQbConnectUrl();
      window.location.assign(url);
    } catch (e) {
      onToast?.(`Could not start OAuth: ${e.detail || 'network error'}`);
      setBusyConnect(false);
    }
  };

  const onRefresh = async (id) => {
    setBusyRefresh(id);
    try {
      const res = await refreshQbConnection(id);
      onToast?.(`Imported ${res.entities.coa_count} accounts, ${res.entities.vendors_count} vendors`);
      await load();
    } catch (e) {
      onToast?.(`Refresh failed: ${e.detail || 'network error'}`);
    } finally {
      setBusyRefresh(null);
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm('Disconnect this QuickBooks company? Mappings will be removed.')) return;
    setBusyDelete(id);
    try {
      await deleteQbConnection(id);
      if (selectedId === id) setSelectedId(null);
      onToast?.('Disconnected');
      await load();
    } catch (e) {
      onToast?.(`Disconnect failed: ${e.detail || 'network error'}`);
    } finally {
      setBusyDelete(null);
    }
  };

  const selectedConn = useMemo(
    () => connections.find((c) => c.id === selectedId) || null,
    [connections, selectedId],
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <SettingsCard
        testid="qb-card"
        title={
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
            {QB_LOGO}
            QuickBooks Online
          </span>
        }
        description={configured
          ? 'Connect one or more QuickBooks companies to import your chart of accounts, vendors, tax codes, and payment accounts.'
          : 'QuickBooks integration is in sandbox-only configuration. Add real Intuit credentials in the backend .env to go live.'}
        actions={
          <button
            type="button"
            data-testid="qb-connect-btn"
            onClick={onConnect}
            disabled={busyConnect}
            style={{ ...primaryBtn(colors, busyConnect), display: 'inline-flex', alignItems: 'center', gap: 8 }}
          >
            <Plus size={14} />
            {connections.length ? 'Connect new company' : 'Connect QuickBooks'}
          </button>
        }
      >
        {loading && (
          <div data-testid="qb-loading" style={{ color: colors.textMuted, fontSize: 13 }}>
            Loading connections…
          </div>
        )}
        {!loading && connections.length === 0 && (
          <div
            data-testid="qb-empty-state"
            style={{
              padding: 20, borderRadius: 12,
              background: colors.bg, border: `1px dashed ${colors.border}`,
              display: 'flex', alignItems: 'center', gap: 14,
            }}
          >
            <div style={{ fontSize: 24 }}>{QB_LOGO}</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: colors.text }}>No QuickBooks company connected yet</div>
              <div style={{ color: colors.textMuted, fontSize: 13, marginTop: 2 }}>
                Click “Connect QuickBooks” above. We’ll open Intuit’s secure consent screen in a new tab.
              </div>
            </div>
          </div>
        )}
        {!loading && connections.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {connections.map((c) => {
              const isSelected = c.id === selectedId;
              return (
                <div
                  key={c.id}
                  data-testid={`qb-row-${c.id}`}
                  onClick={() => setSelectedId(c.id)}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr auto auto',
                    gap: 12,
                    alignItems: 'center',
                    padding: 14,
                    borderRadius: 12,
                    border: `1px solid ${isSelected ? colors.accent : colors.border}`,
                    background: isSelected ? colors.accentSoft : colors.bg,
                    cursor: 'pointer',
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 700, color: colors.text, fontSize: 14 }}>
                      {c.company_name || 'QuickBooks Company'}
                    </div>
                    <div style={{ color: colors.textMuted, fontSize: 12, marginTop: 2, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <span>Realm {c.realm_id.slice(0, 4)}…{c.realm_id.slice(-4)}</span>
                      <span>·</span>
                      <HealthBadge health={c.token_health} colors={colors} />
                      <span>·</span>
                      <span>Last synced {_fmtTime(c.last_refresh_at)}</span>
                    </div>
                  </div>
                  <button
                    type="button"
                    data-testid={`qb-refresh-${c.id}`}
                    onClick={(e) => { e.stopPropagation(); onRefresh(c.id); }}
                    disabled={busyRefresh === c.id}
                    style={{ ...outlineBtn(colors), display: 'inline-flex', alignItems: 'center', gap: 6 }}
                  >
                    <RefreshCw size={13} />
                    {busyRefresh === c.id ? 'Refreshing…' : 'Refresh'}
                  </button>
                  <button
                    type="button"
                    data-testid={`qb-disconnect-${c.id}`}
                    onClick={(e) => { e.stopPropagation(); onDelete(c.id); }}
                    disabled={busyDelete === c.id}
                    style={{
                      ...outlineBtn(colors),
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      color: '#B42318', borderColor: '#F8C8C0',
                    }}
                  >
                    <Unplug size={13} />
                    {busyDelete === c.id ? 'Disconnecting…' : 'Disconnect'}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </SettingsCard>

      {selectedConn && (
        <QuickBooksMappings
          connection={selectedConn}
          connections={connections}
          onSelectConnection={setSelectedId}
          onToast={onToast}
        />
      )}
    </div>
  );
}
